import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface VoiplineSettings {
  ariUrl: string;
  ariUser: string;
  ariPassword: string;
  sipHost: string;
  sipPort: string;
  sipUser: string;
  sipPassword: string;
  sipDomain: string;
  outboundProxy: string;
  transport: string;
  trunkEndpoint: string;
  publicWssUrl: string;
  webhookSecret: string;
  webhookDebugLog: boolean;
}

interface VoiplineSettingsResponse extends VoiplineSettings {
  webhookUrl: string;
}

const EMPTY: VoiplineSettings = {
  ariUrl: '',
  ariUser: '',
  ariPassword: '',
  sipHost: '',
  sipPort: '5060',
  sipUser: '',
  sipPassword: '',
  sipDomain: '',
  outboundProxy: '',
  transport: 'udp',
  trunkEndpoint: 'voipline-trunk',
  publicWssUrl: '',
  webhookSecret: '',
  webhookDebugLog: true,
};

const SENTINEL = '••••••••';

export function VoiplineConfigSection(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['system-config', 'voipline'],
    queryFn: () => api.get<Partial<VoiplineSettingsResponse>>('/system-config/voipline'),
  });

  const [form, setForm] = useState<VoiplineSettings>(EMPTY);
  const [testMessage, setTestMessage] = useState<string | null>(null);
  const [generatedSecret, setGeneratedSecret] = useState<string | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);

  useEffect(() => {
    if (data) {
      const { webhookUrl: _webhookUrl, ...rest } = data;
      setForm({ ...EMPTY, ...rest });
    }
  }, [data]);

  const webhookUrl = data?.webhookUrl ?? '';

  const mutation = useMutation({
    mutationFn: (payload: VoiplineSettings) => api.patch('/system-config/voipline', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-config', 'voipline'] });
      alert('VP SIP trunk settings saved.');
    },
    onError: () => alert('Failed to save VP SIP trunk settings.'),
  });

  const testMutation = useMutation({
    mutationFn: () => api.post<{ ok: boolean; status?: string; error?: string }>('/system-config/voipline/test'),
    onSuccess: (res) => {
      setTestMessage(res.ok
        ? `Trunk endpoint reachable (state: ${res.status ?? 'online'})`
        : (res.error ?? 'Trunk registration failed.'));
    },
    onError: () => setTestMessage('Trunk registration test failed.'),
  });

  const generateMutation = useMutation({
    mutationFn: () => api.post<{ ok: boolean; secret: string }>('/system-config/voipline/generate-webhook-secret'),
    onSuccess: (res) => {
      if (res.ok) {
        setGeneratedSecret(res.secret);
        setSecretCopied(false);
        setForm((f) => ({ ...f, webhookSecret: SENTINEL }));
        queryClient.invalidateQueries({ queryKey: ['system-config', 'voipline'] });
      }
    },
  });

  async function copyGeneratedSecret(): Promise<void> {
    if (!generatedSecret) return;
    try {
      await navigator.clipboard.writeText(generatedSecret);
      setSecretCopied(true);
    } catch {
      setSecretCopied(false);
    }
  }

  const syncMutation = useMutation({
    mutationFn: () => api.post<{
      ok: boolean;
      agentsWritten: number;
      path: string;
      reloaded: boolean;
      error?: string;
    }>('/system-config/voipline/sync-agents'),
    onSuccess: (res) => {
      if (!res.ok) {
        setTestMessage(res.error ?? 'Sync failed.');
        return;
      }
      const reloadNote = res.reloaded ? 'PJSIP reloaded.' : 'File written but PJSIP reload failed — see server logs.';
      setTestMessage(`Wrote ${res.agentsWritten} agent endpoint(s) to ${res.path}. ${reloadNote}`);
    },
    onError: () => setTestMessage('Sync failed (network or auth error).'),
  });

  if (isLoading) return <div>Loading VP SIP trunk config…</div>;

  const update = (key: keyof VoiplineSettings) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  return (
    <section className="card p-4 space-y-4">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">VP SIP trunk</h2>
        <p className="text-sm text-ink-500">
          Browser audio rides WebRTC into the on-premise Asterisk gateway, which dials out across this SIP trunk.
          Configure the trunk credential VP issued you, the gateway's ARI control channel, and the public WSS
          URL the agent's browser should reach Asterisk on.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="ARI URL" placeholder="http://127.0.0.1:8088" value={form.ariUrl} onChange={update('ariUrl')} />
        <Field label="ARI User" value={form.ariUser} onChange={update('ariUser')} />
        <Field label="ARI Password" type="password" value={form.ariPassword} onChange={update('ariPassword')} />
        <Field label="Public WSS URL" placeholder="wss://dialer.example.com/sip-ws/" value={form.publicWssUrl} onChange={update('publicWssUrl')} />

        <Field label="Trunk endpoint name" placeholder="voipline-trunk" value={form.trunkEndpoint} onChange={update('trunkEndpoint')} />

        <Field label="SIP host" placeholder="sip.uk.voipcloud.online" value={form.sipHost} onChange={update('sipHost')} />
        <Field label="SIP port" placeholder="5060" value={form.sipPort} onChange={update('sipPort')} />
        <Field label="SIP domain" placeholder="defaults to SIP host" value={form.sipDomain} onChange={update('sipDomain')} />
        <Field label="Outbound proxy" placeholder="optional" value={form.outboundProxy} onChange={update('outboundProxy')} />

        <div>
          <label className="block text-sm font-medium text-ink-700">Transport</label>
          <select
            className="mt-1 block w-full rounded-md border-ink-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={form.transport}
            onChange={update('transport')}
          >
            <option value="udp">UDP</option>
            <option value="tcp">TCP</option>
            <option value="tls">TLS</option>
          </select>
        </div>

        <Field label="Shared SIP user" value={form.sipUser} onChange={update('sipUser')} />
        <Field label="Shared SIP password" type="password" value={form.sipPassword} onChange={update('sipPassword')} />
      </div>

      <p className="text-xs text-ink-500">
        Per-agent SIP credentials override this shared trunk credential. Set them under Settings → Users,
        then click <span className="font-medium">Sync agents to Asterisk</span> to regenerate
        <span className="font-mono"> /etc/asterisk/pjsip-agents.conf</span> and reload PJSIP.
      </p>

      <div className="border-t border-ink-200 pt-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Webhooks</h3>
        <p className="text-sm text-ink-500 mt-1">
          The portal posts call lifecycle + recording events here. Configure the URL + Secret Token below
          on the upstream portal's webhook config page, tick all 11 event boxes, then click
          <span className="font-medium"> Apply configuration</span> on the PBX tab.
        </p>

        <div className="mt-3 space-y-3">
          <div>
            <label className="block text-sm font-medium text-ink-700">Webhook URL</label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                readOnly
                className="block w-full rounded-md border-ink-300 bg-ink-50 shadow-sm sm:text-sm font-mono"
                value={webhookUrl || '(set PUBLIC_API_URL on the server)'}
              />
              <button
                type="button"
                className="btn-secondary px-3 py-1 text-sm whitespace-nowrap"
                onClick={() => webhookUrl && void navigator.clipboard.writeText(webhookUrl)}
                disabled={!webhookUrl}
              >
                Copy
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Secret token"
              type="password"
              placeholder="paste the same value into the portal"
              value={form.webhookSecret}
              onChange={(e) => setForm((f) => ({ ...f, webhookSecret: e.target.value }))}
            />
            <div className="flex items-end">
              <button
                type="button"
                className="btn-secondary px-3 py-2 text-sm"
                onClick={() => generateMutation.mutate()}
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? 'Generating…' : 'Generate random secret'}
              </button>
            </div>
          </div>

          {generatedSecret && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
              <p className="font-medium text-amber-900">Copy now — only shown once.</p>
              <p className="mt-1 text-xs text-amber-800">
                Paste this into the portal's <span className="font-mono">Secret Token</span> field.
                Subsequent loads of this page will mask it.
              </p>
              <div className="mt-2 flex gap-2">
                <code className="block flex-1 rounded bg-white px-2 py-1 font-mono text-xs break-all">
                  {generatedSecret}
                </code>
                <button
                  type="button"
                  className="btn-secondary px-3 py-1 text-sm whitespace-nowrap"
                  onClick={copyGeneratedSecret}
                >
                  {secretCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-ink-300 text-primary-600 focus:ring-primary-500"
              checked={form.webhookDebugLog}
              onChange={(e) => setForm((f) => ({ ...f, webhookDebugLog: e.target.checked }))}
            />
            <span>Log every accepted payload to <span className="font-mono">WebhookLog</span> table (capture mode)</span>
          </label>
          <p className="text-xs text-ink-500 -mt-2 ml-6">
            Useful while confirming payload shapes from real traffic. Turn off in steady state to save DB churn.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        <button
          type="button"
          className="btn bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
          onClick={() => mutation.mutate(form)}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save settings'}
        </button>
        <button
          type="button"
          className="btn-secondary px-4 py-2"
          onClick={() => testMutation.mutate()}
          disabled={testMutation.isPending}
        >
          {testMutation.isPending ? 'Testing…' : 'Test SIP registration'}
        </button>
        <button
          type="button"
          className="btn-secondary px-4 py-2"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
        >
          {syncMutation.isPending ? 'Syncing…' : 'Sync agents to Asterisk'}
        </button>
      </div>
      {testMessage && <p className="text-sm text-ink-600">{testMessage}</p>}
    </section>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: 'text' | 'password';
  placeholder?: string;
}): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium text-ink-700">{props.label}</label>
      <input
        type={props.type ?? 'text'}
        className="mt-1 block w-full rounded-md border-ink-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        value={props.value}
        onChange={props.onChange}
        placeholder={props.placeholder}
      />
    </div>
  );
}
